import { useRef, useState } from 'react';

interface TiltState {
  x: number;
  y: number;
}

export function useTilt() {
  const [tilt, setTilt] = useState<Record<number, TiltState>>({});
  const handlers = useRef<Record<number, (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => void>>({});

  const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

  const rafThrottle = <T extends (...args: any[]) => void>(fn: T) => {
    let raf: number | null = null;
    let lastArgs: Parameters<T>;
    return (...args: Parameters<T>) => {
      const first = args[0] as { persist?: () => void } | undefined;
      if (first?.persist) first.persist();
      lastArgs = args;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        fn(...lastArgs);
        raf = null;
      });
    };
  };

  const onMouseMove = (index: number) => {
    if (!handlers.current[index]) {
      handlers.current[index] = rafThrottle(
        (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          const target = evt.currentTarget;
          if (!target) return;
          const rect = target.getBoundingClientRect();
          const x = evt.clientX - rect.left;
          const y = evt.clientY - rect.top;
          const midX = rect.width / 2;
          const midY = rect.height / 2;
          const rotY = clamp(((x - midX) / midX) * 10, -8, 8);
          const rotX = clamp(((midY - y) / midY) * 10, -8, 8);
          setTilt((prev) => ({ ...prev, [index]: { x: rotX, y: rotY } }));
        },
      );
    }
    return handlers.current[index];
  };

  const onMouseLeave = (index: number) => () => {
    setTilt((prev) => ({ ...prev, [index]: { x: 0, y: 0 } }));
  };

  return { tilt, onMouseMove, onMouseLeave } as const;
}
